import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AvailabilityService, IRoomOccupancy, ISearchParams, isValidStayLength, SettingsService, StorageService } from 'core-lib';
import dayjs from 'dayjs';
import { forEach } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-deeplink-handler',
    imports: [],
    template: '',
    styles: ''
})
export class DeeplinkHandlerComponent {

  /*tests: 
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=24-04-2025&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=24-04-2025&room0=1|1|10&room1=2|0|0
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=24-04-2025&departure=28-04-2024&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=24-04-2025&nroOfNights=5&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=today&nroOfNights=2&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=tomorrow&nroOfNights=2&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=days30&nroOfNights=3&room0=1|1|10
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&arrival=days15
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN
    http://localhost:4200/deeplink/?lang=fi&hotelCode=1&chainCode=CHAIN&makeSearch=1


  */
  //docs here: https://vitechotellinx.atlassian.net/wiki/spaces/KB/pages/276398228/Varausmoottorin+hakumaskin+integrointi+kotisivuille+ja+deeplinkit


  subs:Subscription[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute, 
    private avService: AvailabilityService, 
    private translateService: TranslateService,
    private settings: SettingsService,
    private storage: StorageService
  ) {
   
  }


  ngOnInit() {
    this.subs.push(
      this.activatedRoute.queryParamMap.subscribe((res: any) => {
        const rawParams = res.params; // string -> string
        const params = this.normalizeQueryParams(rawParams);

        const search: ISearchParams = this.constructSearchParams(params);

        const makeSearch = params['makesearch'];
        const hasUnknownChildAges = search.roomData.some(room => room.childAges.some(age => age === null));
        const hasInvalidStayLength = !isValidStayLength(search.arrival, search.departure);

        // never search without known child ages or with a stay the API rejects:
        // send the user to the searchmask, which asks for the missing ages or new dates
        if (makeSearch && parseInt(makeSearch, 10) > 0 && !hasUnknownChildAges && !hasInvalidStayLength) {
          this.avService.makeSearch(search);
        } else {
          this.avService.setSearchParamsAndNavigateHome(search);
        }
      })
    );
  }

  private normalizeQueryParams(queryParams: Record<string, any>): Record<string, any> {
    const normalized: Record<string, any> = {};

    Object.keys(queryParams || {}).forEach((key) => {
      normalized[key.toLowerCase()] = queryParams[key];
    });

    return normalized;
  }

  constructSearchParams(queryParams: any): ISearchParams {
  const roomdata: IRoomOccupancy[] = [];

  const keys = ['room0', 'room1', 'room2', 'room3'];

  if (!queryParams['room0']) {
    roomdata.push({
      adults: parseInt(this.settings.get('searchMask.defaultAdults')),
      children: 0,
      childAges: []
    });
  }

  keys.forEach((key: string) => {
    if (queryParams[key]) {
      const room = queryParams[key].split('|');
      const children = Math.max(parseInt(room[1], 10) || 0, 0);
      const parsedAges: number[] = room[2]?.length > 0
        ? room[2].split('-').map((ca: string) => parseInt(ca, 10))
        : [];

      // every child must have a known age; missing or invalid ages are null so the user is asked for them
      const childAges: (number | null)[] = Array.from({ length: children }, (_, i) => {
        const age = parsedAges[i];
        return Number.isInteger(age) && age >= 0 ? age : null;
      });

      roomdata.push({
        adults: parseInt(room[0], 10),
        children,
        childAges
      });
    }
  });

  const arrival: Date = this.handleArrivalDate(queryParams?.arrival);

  // default departure is next day
  let departure: Date = this.setNextDay(arrival);

  // nroOfNights (now lowercase)
  const nroOfNights = queryParams['nroofnights'];
  if (nroOfNights) {
    departure = dayjs(arrival).add(parseInt(nroOfNights, 10), 'days').toDate();
  }

  // departure overrides nroOfNights.
  // An invalid stay length (zero, negative or over MAX_STAY_NIGHTS) is kept as is: the searchmask replaces it and asks for new dates.
  const departureQP = queryParams['departure'];
  if (departureQP) {
    departure = this.parseDate(departureQP);
  }

  // sorting (now lowercase)
  const roomPriority = queryParams['roompriorityorder'];
  const ratePriority = queryParams['ratepriorityorder'];
  if (roomPriority) this.storage.saveSessionStorage('roomPriorityOrder', roomPriority);
  if (ratePriority) this.storage.saveSessionStorage('ratePriorityOrder', ratePriority);

  return {
    arrival,
    departure,
    hotelCode: queryParams['hotelcode'],
    category: queryParams['category'] ?? 'ALL',
    roomData: roomdata,
    bookingCode: queryParams['bookingcode'],
    chainCode: queryParams['chaincode'] ?? 'CHAIN'
  };
}


  handleArrivalDate(arrivalString: string): Date {

    const today = dayjs();
    if (!arrivalString) {
      return today.toDate();
    }
    // Convert to lowercase to allow case-insensitive matching.
    const lower = arrivalString.toLowerCase();
  

    // Return today's date.
    if (lower === 'today') {
      return today.toDate();
    }

    // Return tomorrow's date.
    if (lower === 'tomorrow') {
      return today.add(1, 'day').toDate();
    }

    // Check for 'daysX' pattern where X is a number.
    const daysMatch = lower.match(/^days(\d+)$/);
    if (daysMatch) {
      const daysToAdd = parseInt(daysMatch[1], 10);
      return today.add(daysToAdd, 'day').toDate();
    }

    // Default to date string.
    return this.parseDate(arrivalString);
  }

  parseDate(dateString:string):Date {
    let d = dateString.split('-');
    return new Date(parseInt(d[2]), parseInt(d[1])-1, parseInt(d[0]));
  }

  setNextDay(date:string|Date):Date {
    let d = date;
    if (typeof date == 'string') {
      d = this.parseDate(date);
    }
    return dayjs(d).add(1, 'day').toDate();
  }

  ngOnDestroy() {
    this.subs.forEach((sub:Subscription) => {
      sub.unsubscribe();
    });
  }
}
